import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useHouseLatest } from "./hooks/useHouseLatest";
import { useSenateLatest } from "./hooks/useSenateLatest";
import { hapticSelection } from "../../lib/haptics";
import { roundedPanelSx } from "../Dashboard/overviewPanel";
import {
  ChamberControl,
  DisclosurePagination,
  DisclosureSummary,
  SectionTitle,
  WorkspaceState,
  WorkspaceHeader,
} from "./components/InvestorWorkspace";
import { CongressionalTradeTable } from "./components/CongressionalTradeTable";

import dayjs from "dayjs";

interface CongressionalTradesProps {
  navigation?: React.ReactNode;
}
const CongressionalTrades = ({ navigation }: CongressionalTradesProps) => {
  const theme = useTheme();
  const [chamber, setChamber] = useState<"house" | "senate">("house");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const house = useHouseLatest({
    page,
    limit: pageSize,
    enabled: chamber === "house",
  });
  const senate = useSenateLatest({
    page,
    limit: pageSize,
    enabled: chamber === "senate",
  });
  const query = chamber === "house" ? house : senate;
  const allTrades = query.data?.data ?? [];
  const latest = allTrades.reduce(
    (date, trade) =>
      !date || dayjs(trade.disclosureDate).isAfter(dayjs(date))
        ? trade.disclosureDate
        : date,
    "",
  );
  return (
    <>
      <WorkspaceHeader
        title="Latest Trade Disclosures"
        navigation={navigation}
        actions={
          !query.isLoading &&
          !query.isError && (
            <DisclosureSummary
              count={allTrades.length}
              latest={latest}
              pageScoped
            />
          )
        }
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <SectionTitle>Disclosures</SectionTitle>
        <Box sx={{ ml: "auto" }}>
          <ChamberControl
            value={chamber}
            onChange={(next) => {
              if (next === "all") return;
              hapticSelection();
              setChamber(next);
              setPage(0);
            }}
          />
        </Box>
      </Box>
      {query.isLoading && <WorkspaceState loading />}
      {!query.isLoading && query.isError && (
        <WorkspaceState
          message="Unable to load trading disclosures."
          retry={() => {
            void query.refetch();
          }}
        />
      )}
      {!query.isLoading && !query.isError && (
        <Box
          component="section"
          aria-label="Disclosures"
          data-disclosure-panel
          sx={{
            ...roundedPanelSx(theme.palette.mode === "dark"),
            scrollMarginTop: "calc(80px + env(safe-area-inset-top, 0px))",
            "& .disclosure-label": {
              fontSize: "0.8125rem",
              color: "text.secondary",
              textTransform: "none",
              letterSpacing: 0,
              mb: 0.5,
            },
          }}
        >
          <CongressionalTradeTable trades={allTrades} />
          <DisclosurePagination
            page={page}
            pageSize={pageSize}
            count={allTrades.length}
            hasNext={query.data?.pagination.hasNextPage ?? false}
            busy={query.isFetching}
            onPage={(next) => {
              hapticSelection();
              setPage(next);
            }}
            onPageSize={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        Amounts are disclosed ranges. Publication dates may be later than trade
        dates.
      </Typography>
    </>
  );
};
export default CongressionalTrades;
